// Issue recording page with chat → summary review → save + auto-resolve pipeline
import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { IssueRecordingChat } from '@/components/chat/IssueRecordingChat'
import { IssueSummaryReview } from '@/components/chat/IssueSummaryReview'
import { IssueAnalysisPanel } from '@/components/chat/IssueAnalysisPanel'
import { DuplicateDetection } from '@/components/chat/DuplicateDetection'
import { CustomerContextSelector } from '@/components/chat/CustomerContextSelector'
import { useDuplicateDetection } from '@/hooks/useDuplicateDetection'
import { useCustomers, useCustomerEntities, useCustomerUsers } from '@/hooks/useCustomers'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { getExpertFeatureForIssue, getExpertiseDomain } from '@/lib/expert-module-map'
import type { IssueRecorderOutput } from '@/types/ai'
import type { ChatMessage } from '@/types/issue'

type Phase = 'chat' | 'summary'

export function IssueRecording(): JSX.Element {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [phase, setPhase] = useState<Phase>('chat')
  const [extractedData, setExtractedData] = useState<IssueRecorderOutput['extractedData']>({})
  const [completeness, setCompleteness] = useState(0)
  const [saving, setSaving] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const uploadedDocIdsRef = useRef<string[]>([])
  const chatMessagesRef = useRef<ChatMessage[]>([])

  const [customerId, setCustomerId] = useState<string | null>(null)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [customerUserId, setCustomerUserId] = useState<string | null>(null)

  // Load customer profile data for the selected customer
  const { customers } = useCustomers()
  const { entities } = useCustomerEntities(customerId)
  const { users: customerUsers } = useCustomerUsers(customerId)

  const selectedCustomer = customers.find((c) => c.id === customerId) ?? null
  const selectedEntity = entities.find((e) => e.id === entityId) ?? null
  const selectedUser = customerUsers.find((u) => u.id === customerUserId) ?? null

  const { duplicates, similarResolved, isDetecting } = useDuplicateDetection({
    extractedData, enabled: true,
  })

  const handleDataExtracted = (data: IssueRecorderOutput['extractedData']): void => {
    setExtractedData((prev) => ({ ...prev, ...data }))
  }

  const handleShowSummary = () => {
    if (!customerId) {
      alert('Please select a customer before saving.')
      return
    }
    setPhase('summary')
  }

  const handleSaveIssue = async (summaryData: Record<string, string>): Promise<void> => {
    if (!user || saving) return
    setSaving(true)

    try {
      const zeroEmbedding = new Array(1536).fill(0)

      const { data: issueData, error: issueError } = await supabase.from('issues').insert({
        organization_id: user.organizationId,
        created_by: user.id,
        title: summaryData.title || extractedData.problemTitle || 'Untitled Issue',
        description: summaryData.description || extractedData.problemDescription || '',
        description_embedding: JSON.stringify(zeroEmbedding),
        d365_version: summaryData.d365Version || extractedData.d365Version || selectedEntity?.d365_version || 'unknown',
        d365_module: summaryData.d365Module || null,
        d365_deployment_type: extractedData.d365DeploymentType || selectedEntity?.d365_deployment_type || null,
        customer_name: selectedCustomer?.name || extractedData.companyName || null,
        customer_email: extractedData.customerEmail || selectedCustomer?.contact_email || null,
        customer_id: customerId,
        entity_id: entityId,
        customer_user_id: customerUserId,
        category: summaryData.category || 'bug',
        priority: summaryData.priority || 'medium',
        expertise_domain: getExpertiseDomain(getExpertFeatureForIssue(summaryData.d365Module || null, summaryData.category || null)),
        status: 'open',
        ai_analysis: {
          stepsToReproduce: summaryData.stepsToReproduce,
          resolutionPipelineStatus: 'pending',
        },
      } as never).select('id').single()

      if (issueError) throw issueError

      const messages = chatMessagesRef.current
      if (issueData?.id && messages.length > 0) {
        await supabase.from('issue_conversations').insert(
          messages.map((msg) => ({
            issue_id: issueData.id,
            organization_id: user.organizationId,
            user_id: msg.role === 'user' ? user.id : null,
            role: msg.role,
            content: msg.content,
            is_internal: false,
            metadata: {},
          })) as never[]
        )
      }

      // Link uploaded documents to the issue
      const docIds = uploadedDocIdsRef.current
      if (issueData?.id && docIds.length > 0) {
        await supabase.from('customer_profile_documents')
          .update({ issue_id: issueData.id } as never)
          .in('id', docIds)
      }

      draftSavedRef.current = true // Prevent auto-save on unmount after navigate
      navigate(`/issues/${issueData?.id ?? ''}`)
    } catch (err) {
      console.error('Failed to save issue:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveDraft = async (): Promise<void> => {
    if (!user || saving) return
    setSaving(true)
    try {
      const zeroEmbedding = new Array(1536).fill(0)
      const { data: issueData, error: issueError } = await supabase.from('issues').insert({
        organization_id: user.organizationId,
        created_by: user.id,
        title: extractedData.problemTitle || 'Draft Issue',
        description: extractedData.problemDescription || '',
        description_embedding: JSON.stringify(zeroEmbedding),
        d365_version: extractedData.d365Version || selectedEntity?.d365_version || 'unknown',
        d365_module: null,
        d365_deployment_type: extractedData.d365DeploymentType || selectedEntity?.d365_deployment_type || null,
        customer_name: selectedCustomer?.name || extractedData.companyName || null,
        customer_email: extractedData.customerEmail || selectedCustomer?.contact_email || null,
        customer_id: customerId,
        entity_id: entityId,
        customer_user_id: customerUserId,
        category: 'bug',
        priority: 'medium',
        status: 'draft',
        ai_analysis: { resolutionPipelineStatus: 'pending', isDraft: true },
      } as never).select('id').single()

      if (issueError) throw issueError

      // Save conversation history
      const messages = chatMessagesRef.current
      if (issueData?.id && messages.length > 0) {
        await supabase.from('issue_conversations').insert(
          messages.map((msg) => ({
            issue_id: issueData.id, organization_id: user.organizationId,
            user_id: msg.role === 'user' ? user.id : null,
            role: msg.role, content: msg.content, is_internal: false, metadata: {},
          })) as never[]
        )
      }

      // Link documents
      const docIds = uploadedDocIdsRef.current
      if (issueData?.id && docIds.length > 0) {
        await supabase.from('customer_profile_documents')
          .update({ issue_id: issueData.id } as never).in('id', docIds)
      }

      navigate(`/issues/${issueData?.id ?? ''}`)
    } catch (err) {
      console.error('Failed to save draft:', err)
    } finally {
      setSaving(false)
    }
  }

  // Auto-save as draft when navigating away (if there's conversation content)
  const draftSavedRef = useRef(false)
  const autoSaveDraft = useCallback(async () => {
    const messages = chatMessagesRef.current
    if (draftSavedRef.current || saving || !user || messages.length <= 1) return
    if (!extractedData.problemTitle && !extractedData.problemDescription) return
    draftSavedRef.current = true

    try {
      const zeroEmbedding = new Array(1536).fill(0)
      const { data: issueData, error: issueError } = await supabase.from('issues').insert({
        organization_id: user.organizationId,
        created_by: user.id,
        title: extractedData.problemTitle || 'Draft Issue (Auto-saved)',
        description: extractedData.problemDescription || '',
        description_embedding: JSON.stringify(zeroEmbedding),
        d365_version: extractedData.d365Version || selectedEntity?.d365_version || 'unknown',
        d365_module: null,
        d365_deployment_type: extractedData.d365DeploymentType || selectedEntity?.d365_deployment_type || null,
        customer_name: selectedCustomer?.name || extractedData.companyName || null,
        customer_email: extractedData.customerEmail || selectedCustomer?.contact_email || null,
        customer_id: customerId,
        entity_id: entityId,
        customer_user_id: customerUserId,
        category: 'bug',
        priority: 'medium',
        status: 'draft',
        ai_analysis: { resolutionPipelineStatus: 'pending', isDraft: true, autoSaved: true },
      } as never).select('id').single()

      // Without this the insert error was discarded, so a failed auto-save
      // logged "Auto-saved draft: undefined" and looked like a success.
      if (issueError) throw issueError

      if (issueData?.id && messages.length > 0) {
        await supabase.from('issue_conversations').insert(
          messages.map((msg) => ({
            issue_id: issueData.id, organization_id: user.organizationId,
            user_id: msg.role === 'user' ? user.id : null,
            role: msg.role, content: msg.content, is_internal: false, metadata: {},
          })) as never[]
        )
      }

      const docIds = uploadedDocIdsRef.current
      if (issueData?.id && docIds.length > 0) {
        await supabase.from('customer_profile_documents')
          .update({ issue_id: issueData.id } as never).in('id', docIds)
      }
      console.log('[IssueRecording] Auto-saved draft:', issueData?.id)
    } catch (err) {
      // The guard flag is set before the attempt; clear it on failure so a
      // later unload/unmount can retry instead of silently dropping the draft.
      draftSavedRef.current = false
      console.warn('[IssueRecording] Auto-save draft failed:', err)
    }
  }, [user, saving, extractedData, selectedEntity, selectedCustomer, customerId, entityId, customerUserId])

  // Save draft on page unload
  useEffect(() => {
    const handleBeforeUnload = () => { autoSaveDraft() }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      // Also auto-save when component unmounts (navigation)
      autoSaveDraft()
    }
  }, [autoSaveDraft])

  // Mark as saved when user manually saves (prevent double-save)
  const handleSaveDraftManual = async () => {
    draftSavedRef.current = true
    await handleSaveDraft()
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-kaycoo-teal-dark">Record New Issue</h1>
          <p className="text-sm text-kaycoo-charcoal-2">Describe the issue, attach documents, and the AI will analyze it</p>
        </div>
        {phase === 'chat' && (
          <div className="flex gap-2 shrink-0">
            <button
              onClick={handleSaveDraftManual}
              disabled={saving}
              className="rounded-lg border border-kaycoo-teal px-4 py-2 text-sm font-medium text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save Draft
            </button>
            <button
              onClick={handleShowSummary}
              disabled={saving || !customerId}
              className="rounded-lg bg-kaycoo-teal px-6 py-2 text-sm font-medium text-white hover:bg-kaycoo-teal-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              Review & Save
            </button>
          </div>
        )}
      </div>

      <CustomerContextSelector
        customerId={customerId} entityId={entityId} userId={customerUserId}
        onCustomerChange={setCustomerId} onEntityChange={setEntityId} onUserChange={setCustomerUserId}
      />

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <div className="h-[600px]">
            {phase === 'chat' ? (
              customerId && !selectedCustomer ? (
                <div className="flex h-full items-center justify-center rounded-lg border border-kaycoo-teal-light bg-kaycoo-white">
                  <p className="text-sm text-kaycoo-charcoal-2">Loading customer profile...</p>
                </div>
              ) : (
              <IssueRecordingChat
                onDataExtracted={handleDataExtracted}
                onComplete={handleShowSummary}
                onMessagesChange={(m) => { chatMessagesRef.current = m }}
                onFilesUploaded={(files) => setUploadedFiles((prev) => [...prev, ...files])}
                onDocumentIdsCreated={(ids) => { uploadedDocIdsRef.current.push(...ids) }}
                onCompletenessChange={setCompleteness}
                customerId={customerId}
                organizationId={user?.organizationId ?? null}
                currentExtractedData={extractedData}
                customer={selectedCustomer}
                entity={selectedEntity}
                reportedBy={selectedUser}
              />
              )
            ) : (
              <div className="h-full overflow-y-auto rounded-lg border border-kaycoo-teal-light bg-kaycoo-white">
                <IssueSummaryReview
                  extractedData={extractedData}
                  onConfirm={handleSaveIssue}
                  onBack={() => setPhase('chat')}
                  isSaving={saving}
                />
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4 lg:col-span-2">
          <IssueAnalysisPanel
            extractedData={extractedData}
            completeness={completeness}
            uploadedFiles={uploadedFiles}
          />
          <DuplicateDetection
            duplicates={duplicates}
            similarResolved={similarResolved}
            isDetecting={isDetecting}
          />
        </div>
      </div>
    </div>
  )
}
